import {
  BlackPearlVesting__factory,
  Dex__factory,
  Erc20Token__factory,
  GoldYield__factory,
  Mine__factory,
  PearlStore__factory,
  PearlYield__factory
} from 'public/abi/evm/types';
import useProvider from '~/composables/useProvider';
import { useWeb3ModalAccount } from '@web3modal/ethers/vue';
import { TransactionReceipt, formatEther, parseUnits } from 'ethers';
import { useEnvs, useMainStore } from '#imports';
import { Tokens } from '~/utils/constants';

export default () => {
  const store = useMainStore();
  const { blockchain } = useEnvs();
  const { address } = useWeb3ModalAccount();
  const { getProvider } = useProvider();

  const liquidityShare = async () => {
    const amount = await PearlStore__factory.connect(
      blockchain.contracts[store.currentToken].addresses.store,
      getProvider()
    ).liquidityShare();

    return formatEther(amount);
  };

  const lockPeriod = (tokenName: Tokens = store.currentToken) => {
    const map: Record<string, () => Promise<bigint>> = {};

    Object.values(Tokens).forEach((item) => {
      map[item] = (item === Tokens.gold ? GoldYield__factory : PearlYield__factory).connect(
        blockchain.contracts[item].addresses.yield,
        getProvider()
      ).lockPeriod;
    });

    map[Tokens.blackPearl] = BlackPearlVesting__factory.connect(
      blockchain.contracts.blackPearlVesting,
      getProvider()
    ).cliff;

    return map[tokenName]();
  };

  const unlockable = async (address: string, index: number, tokenName: Tokens = store.currentToken) => {
    const map: Record<string, () => Promise<bigint | number>> = {};

    Object.values(Tokens).forEach((item) => {
      const contractAddress = blockchain.contracts[item].addresses.yield;

      if (!contractAddress) {
        map[item] = () => Promise.resolve(0);
        return;
      }
      const contract = (item === Tokens.gold ? GoldYield__factory : PearlYield__factory).connect(
        contractAddress,
        getProvider()
      );
      map[item] = () => contract.unlockable(address, index);
    });

    map[Tokens.blackPearl] = BlackPearlVesting__factory.connect(
      blockchain.contracts.blackPearlVesting,
      getProvider()
    ).withdrawableAmount.bind({}, address, index);

    return formatEther(await map[tokenName]());
  };

  const unlockPeriod = async (tokenName: Tokens = store.currentToken) => {
    const map: Record<string, () => Promise<bigint>> = {};

    Object.values(Tokens).forEach((item) => {
      map[item] = (item === Tokens.gold ? GoldYield__factory : PearlYield__factory).connect(
        blockchain.contracts[item].addresses.yield,
        getProvider()
      ).unlockPeriod;
    });

    map[Tokens.blackPearl] = BlackPearlVesting__factory.connect(
      blockchain.contracts.blackPearlVesting,
      getProvider()
    ).unlockPeriod;

    return map[tokenName]();
  };

  const createErc20 = (address: string) => Erc20Token__factory.connect(address, getProvider());

  const availableTokens = async (address: string, tokenAddress: string) => {
    return formatEther(await createErc20(tokenAddress).balanceOf(address));
  };

  const allowance = async (address: string, currencyAddress: string, storeTokenName?: Tokens) => {
    const contractAddr = blockchain.contracts[storeTokenName || store.currentToken].addresses;
    const storeOrMine = storeTokenName === Tokens.pearl ? contractAddr.store : contractAddr.mine;

    return formatEther(await createErc20(currencyAddress).allowance(address, String(storeOrMine)));
  };

  const allowanceShop = async (address: string) => {
    return formatEther(await createErc20(blockchain.contracts.usdt).allowance(address, blockchain.contracts.shop));
  };

  const allowanceBundles = async (address: string) => {
    return formatEther(await createErc20(blockchain.contracts.usdt).allowance(address, blockchain.contracts.bundles));
  };

  const allowanceNlaam = async (address: string) => {
    if (!blockchain.contracts.nlaam || blockchain.contracts.nlaam === '0x0000000000000000000000000000000000000000') {
      return '0';
    }
    return formatEther(await createErc20(blockchain.contracts.usdt).allowance(address, blockchain.contracts.nlaam));
  };

  const totalStake = async (tokenName: Tokens = store.currentToken) => {
    const amount = await (tokenName === Tokens.pearl ? PearlYield__factory : Mine__factory)
      .connect(
        blockchain.contracts[tokenName].addresses[tokenName === Tokens.pearl ? 'yield' : 'mine'] as string,
        getProvider()
      )
      .totalSupply();

    return formatEther(amount);
  };

  const totalStakingRewards = async () => {
    return formatEther(
      await PearlYield__factory.connect(blockchain.contracts.pearl.addresses.yield, getProvider()).totalReward()
    );
  };

  const staked = async (address: string, tokenName: Tokens = store.currentToken) => {
    const map: Record<string, () => Promise<bigint>> = {};

    Object.values(Tokens).forEach((item) => {
      const factory = tokenName === Tokens.pearl ? PearlYield__factory : Mine__factory;

      const contract = blockchain.contracts[tokenName].addresses[tokenName === Tokens.pearl ? 'yield' : 'mine'];
      if (!contract) return () => 0;
      map[item] = factory.connect(String(contract), getProvider()).balanceOf.bind({}, address);
    });

    map[Tokens.blackPearl] = BlackPearlVesting__factory.connect(
      blockchain.contracts.blackPearlVesting,
      getProvider()
    ).balanceOf.bind({}, address);

    return formatEther(await map[tokenName]());
  };

  const earned = async (address: string, tokenName: Tokens = store.currentToken) => {
    if (tokenName != Tokens.pearl) return '0';

    return formatEther(
      await PearlYield__factory.connect(blockchain.contracts[tokenName].addresses.yield, getProvider()).earned(address)
    );
  };

  const saleAmount = async (tokenName: Tokens = store.currentToken) => {
    const factory = tokenName === Tokens.pearl ? PearlStore__factory : Mine__factory;

    const amount = await factory
      .connect(
        String(blockchain.contracts[tokenName].addresses[tokenName === Tokens.pearl ? 'store' : 'mine']),
        getProvider()
      )
      .saleAmount();

    return formatEther(amount);
  };

  const totalClaimed = async (tokenName: Tokens = store.currentToken): Promise<string> => {
    const factory = tokenName === Tokens.pearl ? PearlYield__factory : Mine__factory;

    return formatEther(
      await factory
        .connect(
          String(blockchain.contracts[tokenName].addresses[tokenName === Tokens.pearl ? 'yield' : 'mine']),
          getProvider()
        )
        .totalClaimed()
    );
  };

  /**
   * Retrieves the data of the soldAmount method for a given token by calling the corresponding smart contract method.
   * Method soldAmount implemented in 'mine' and more info we can see in abi.json
   *
   * @param {Tokens} [tokenName=store.currentToken] - The name of the token for which the sold amount is to be fetched. Defaults to the
   *   current token stored.
   * @returns {Promise<string>} A promise that resolves to the sold amount as a formatted string in ether.
   */

  const soldAmount = async (tokenName: Tokens = store.currentToken): Promise<string> => {
    const amount = await Mine__factory.connect(
      String(blockchain.contracts[tokenName].addresses[tokenName === Tokens.pearl ? 'store' : 'mine']),
      getProvider()
    ).soldAmount();

    return formatEther(amount);
  };

  const tokenMaxTotalSupply = async (tokenName: Tokens = store.currentToken) => {
    const tokenMiningAddress = blockchain.contracts[tokenName].addresses.mine!;
    const pearlTokenAddress = blockchain.contracts[Tokens.pearl].addresses.yield;

    const factory = tokenName === Tokens.pearl ? PearlYield__factory : Mine__factory;

    return factory
      .connect(tokenName === Tokens.pearl ? pearlTokenAddress : tokenMiningAddress, getProvider())
      .totalSupply();
  };

  const liquidityAmount = async () => {
    const tokenMiningAddress = blockchain.contracts[store.currentToken].addresses.mine!;
    const pearlTokenAddress = blockchain.contracts[Tokens.pearl].addresses.store;

    if (store.currentToken === Tokens.pearl) {
      return formatEther(await PearlStore__factory.connect(pearlTokenAddress, getProvider()).liquidityAmount());
    }

    return formatEther(await Mine__factory.connect(tokenMiningAddress, getProvider()).liquidityShare());
  };

  const getReleaseAmount = async (receipt: TransactionReceipt) => {
    const currentTokenYield = PearlYield__factory.connect(
      blockchain.contracts[store.currentToken].addresses.yield,
      getProvider()
    );

    const blackPearlContract = BlackPearlVesting__factory.connect(
      blockchain.contracts.blackPearlVesting,
      getProvider()
    );

    const currentBlock = receipt.blockNumber;

    const releaseLog = await Promise.any([
      currentTokenYield
        ?.queryFilter(
          currentTokenYield.filters.StakeRelease(address.value, undefined, undefined),
          currentBlock - 10000,
          currentBlock
        )
        .then((logs: any[]) => logs.find((log: { transactionHash: string }) => log.transactionHash === receipt.hash)),
      blackPearlContract
        ?.queryFilter(
          blackPearlContract.filters.BlackPearlRelease(address.value, undefined, undefined),
          currentBlock - 10000,
          currentBlock
        )
        .then((logs: any[]) => logs.find((log: { transactionHash: string }) => log.transactionHash === receipt.hash))
    ]);

    if (releaseLog && 'args' in releaseLog) {
      return formatEther(releaseLog.args?.amount ?? '0');
    }

    return null;
  };

  const getUnlockAmount = async (receipt: TransactionReceipt) => {
    const currentTokenYield = PearlYield__factory.connect(
      blockchain.contracts[store.currentToken].addresses.yield,
      getProvider()
    );

    const blackPearlContract = BlackPearlVesting__factory.connect(
      blockchain.contracts.blackPearlVesting,
      getProvider()
    );

    const currentBlock = receipt.blockNumber;

    const releaseLog = await Promise.any([
      currentTokenYield
        ?.queryFilter(
          currentTokenYield.filters.StakeUnlock(address.value, undefined, undefined),
          currentBlock - 10000,
          currentBlock
        )
        .then((logs: any[]) => logs.find((log: { transactionHash: string }) => log.transactionHash === receipt.hash)),
      blackPearlContract
        ?.queryFilter(
          blackPearlContract.filters.BlackPearlWithdraw(address.value, undefined, undefined),
          currentBlock - 10000,
          currentBlock
        )
        .then((logs: any[]) => logs.find((log: { transactionHash: string }) => log.transactionHash === receipt.hash))
    ]);

    if (releaseLog && 'args' in releaseLog) {
      return formatEther(releaseLog.args?.amount ?? '0');
    }

    return null;
  };

  const getAmountsOut = async (amount: string, tokenIn: string, tokenOut: string) => {
    const amountDecimal = parseUnits(amount);

    const dex = Dex__factory.connect(blockchain.contracts.dex, getProvider());

    return dex.getAmountsOut(amountDecimal, [tokenIn, tokenOut]);
  };

  return {
    liquidityShare,
    lockPeriod,
    unlockable,
    unlockPeriod,
    availableTokens,
    allowance,
    totalStake,
    totalStakingRewards,
    tokenMaxTotalSupply,
    staked,
    earned,
    saleAmount,
    soldAmount,
    totalClaimed,
    liquidityAmount,
    getReleaseAmount,
    getUnlockAmount,
    allowanceShop,
    allowanceBundles,
    allowanceNlaam,
    getAmountsOut
  };
};
